import { format } from 'date-fns'
import type { FC } from 'react'
import React from 'react'
import styled from 'styled-components'

import Checkbox from '@/components/Checkbox'
import H3 from '@/components/H3'
import type { AdverseCredit } from '@/types/adverseCredit'
import {
  AdverseCreditState,
  AdverseCreditDeclaration,
  AdverseCreditType,
} from '@/types/adverseCredit'

import AdverseCreditsFields from './AdverseCreditsFields'
import type { Fields as AdverseCreditsFieldsT } from './AdverseCreditsFields'

export interface Fields {
  identityConfirmed: boolean
  residencyConfirmed: boolean
  ccjs: AdverseCreditsFieldsT
  ivaBankruptcies: AdverseCreditsFieldsT
  other: AdverseCreditsFieldsT
}

interface Props {
  values: Fields
  onChangeValues: (fields: Fields) => void
}

const Container = styled.div`
  display: grid;
  gap: 40px;
`

const CheckboxContainer = styled.div`
  display: grid;
  gap: 15px;
`

export const defaultValues: Omit<AdverseCredit, 'type'> = {
  state: AdverseCreditState.active,
  declaration: AdverseCreditDeclaration.undeclared,
  amount: 0,
  courtDate: format(new Date(), 'yyyy-MM-dd'),
  caseNumber: '',
}

export const defaultCCJ: AdverseCredit = {
  ...defaultValues,
  type: AdverseCreditType.ccj,
}

export const defaultIvaBankruptcy: AdverseCredit = {
  ...defaultValues,
  type: AdverseCreditType.ivaBankruptcy,
}

export const defaultOther: AdverseCredit = {
  ...defaultValues,
  type: AdverseCreditType.other,
}

const StyledHR = styled.hr`
  margin: 0;
`

const VerifyCreditFields: FC<Props> = ({ values, onChangeValues }) => (
  <Container>
    <div>
      <CheckboxContainer>
        <Checkbox
          label="Identity confirmations"
          checked={values.identityConfirmed}
          onChange={e =>
            onChangeValues({
              ...values,
              identityConfirmed: e.target.checked,
            })
          }
        />
        <Checkbox
          label="Residency confirmations"
          checked={values.residencyConfirmed}
          onChange={e =>
            onChangeValues({
              ...values,
              residencyConfirmed: e.target.checked,
            })
          }
        />
      </CheckboxContainer>
    </div>
    <div>
      <H3>CCJ Type</H3>
      <AdverseCreditsFields
        values={values.ccjs}
        onChangeValues={ccjs =>
          onChangeValues({
            ...values,
            ccjs,
          })
        }
        onAdd={() =>
          onChangeValues({
            ...values,
            ccjs: [...values.ccjs, defaultCCJ],
          })
        }
      />
    </div>
    <StyledHR />
    <div>
      <H3>IVA/ Bankruptcy</H3>
      <AdverseCreditsFields
        values={values.ivaBankruptcies}
        onChangeValues={ivaBankruptcies =>
          onChangeValues({
            ...values,
            ivaBankruptcies,
          })
        }
        onAdd={() =>
          onChangeValues({
            ...values,
            ivaBankruptcies: [...values.ivaBankruptcies, defaultIvaBankruptcy],
          })
        }
      />
    </div>
    <StyledHR />
    <div>
      <H3>Other</H3>
      <AdverseCreditsFields
        values={values.other}
        onChangeValues={other =>
          onChangeValues({
            ...values,
            other,
          })
        }
        onAdd={() =>
          onChangeValues({
            ...values,
            other: [...values.other, defaultOther],
          })
        }
        disableEditing
      />
    </div>
  </Container>
)

export default VerifyCreditFields
